import React from 'react';
import {
    Box,
    Typography,
    Chip,
    Grid,
    Paper,
    Stack,
    Button
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import MemoryIcon from '@mui/icons-material/Memory';
import PublicIcon from '@mui/icons-material/Public';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';

const FilterSection = ({ icon: Icon, title, options, selectedValue, onSelect, multi = false }) => {
    const theme = useTheme();

    const handleSelect = (option) => {
        if (multi) {
            const isAll = option.includes('All ');
            if (isAll) {
                onSelect([]);
            } else {
                const current = Array.isArray(selectedValue) ? selectedValue : [];
                const alreadySelected = current.includes(option);
                const next = alreadySelected
                    ? current.filter(val => val !== option)
                    : [...current, option];
                onSelect(next);
            }
        } else {
            onSelect(option);
        }
    };

    return (
        <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Icon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                    {title}
                </Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {options.map((option) => {
                    const isAll = option.includes('All ');
                    const isSelected = multi 
                        ? (isAll ? (Array.isArray(selectedValue) && selectedValue.length === 0) : (Array.isArray(selectedValue) && selectedValue.includes(option)))
                        : selectedValue === option;

                    return (
                        <Chip
                            key={option}
                            label={option}
                            onClick={() => handleSelect(option)}
                            sx={{
                                backgroundColor: isSelected ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.05)',
                                color: isSelected ? theme.palette.common.white : 'text.secondary',
                                fontWeight: isSelected ? 600 : 400,
                                mb: 1,
                                '&:hover': {
                                    backgroundColor: isSelected ? theme.palette.primary.dark : 'rgba(0, 0, 0, 0.1)',
                                },
                                borderRadius: '12px',
                                px: 0.5,
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease'
                            }}
                        />
                    );
                })}
            </Stack>
        </Box>
    );
};

const ProjectFilters = ({ 
    activeFilters, 
    onFilterChange, 
    onClearAll,
    categories = [],
    technologies = [],
    domains = [],
    countries = []
}) => {
    const theme = useTheme();

    const categoryOptions = ['All Categories', ...(Array.isArray(categories) ? categories.map(c => c.name) : [])];
    const techOptions = ['All Technologies', ...(Array.isArray(technologies) ? technologies : [])];
    const domainOptions = ['All Domains', ...(Array.isArray(domains) ? domains : [])];
    const countryOptions = ['All Countries', ...(Array.isArray(countries) ? countries : [])];

    return (
        <Paper
            elevation={2}
            sx={{
                p: { xs: 2, md: 3 },
                mt: 2,
                borderRadius: 4,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                    Filter Projects
                </Typography>
                <Button
                    startIcon={<CloseIcon sx={{ fontSize: 18 }} />}
                    onClick={onClearAll}
                    sx={{
                        textTransform: 'none',
                        color: 'text.secondary',
                        '&:hover': {
                            backgroundColor: 'transparent',
                            color: theme.palette.primary.main
                        }
                    }}
                >
                    Clear All
                </Button>
            </Stack>

            <Grid container spacing={4}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FilterSection
                        title="Category"
                        icon={LanguageIcon}
                        options={categoryOptions}
                        selectedValue={activeFilters.category}
                        onSelect={(val) => onFilterChange('category', val)}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FilterSection
                        title="Technology"
                        icon={MemoryIcon}
                        options={techOptions}
                        selectedValue={activeFilters.technology}
                        onSelect={(val) => onFilterChange('technology', val)}
                        multi
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FilterSection
                        title="Domain"
                        icon={PublicIcon}
                        options={domainOptions}
                        selectedValue={activeFilters.domain}
                        onSelect={(val) => onFilterChange('domain', val)}
                        multi
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FilterSection
                        title="Country"
                        icon={LocationOnIcon}
                        options={countryOptions}
                        selectedValue={activeFilters.country}
                        onSelect={(val) => onFilterChange('country', val)}
                        multi
                    />
                </Grid>
            </Grid>
        </Paper>
    );
};

export default ProjectFilters;